/**
 * Akış simulation — discrete, deterministic, one shooter on the conveyor at a time.
 *
 * Conveyor ring (clockwise, entered at the bottom-left corner): left side going up, top going
 * right, right side going down, bottom going left. 4*size steps; step p looks inward along one row
 * or column and shoots the FIRST block in line of sight if it matches the shooter's color. Blocks
 * of other colors in front block the shot. One shot per step, 1 ammo per shot.
 */
import { matchesAnswer, normalizeAnswer, type Picture } from "@enjoyment/core";
import type { Action, ActionResult, Level, Shooter, Shot, State } from "./types";

export const SIDE_LEFT = 0;
export const SIDE_TOP = 1;
export const SIDE_RIGHT = 2;
export const SIDE_BOTTOM = 3;

export interface StepPos {
  side: 0 | 1 | 2 | 3;
  /** Row index for left/right sides, column index for top/bottom sides. */
  line: number;
}

/** Where conveyor step p (0..4*size-1) is and which line it looks along. */
export function stepPos(size: number, p: number): StepPos {
  const side = Math.floor(p / size) as 0 | 1 | 2 | 3;
  const k = p % size;
  switch (side) {
    case 0:
      return { side: 0, line: size - 1 - k };
    case 1:
      return { side: 1, line: k };
    case 2:
      return { side: 2, line: k };
    default:
      return { side: 3, line: size - 1 - k };
  }
}

/** First non-cleared cover cell in line of sight of step p, or -1. */
export function rayTarget(cover: Int8Array, size: number, p: number): number {
  const side = Math.floor(p / size);
  const k = p % size;
  if (side === 0) {
    const row = (size - 1 - k) * size;
    for (let c = 0; c < size; c++) if (cover[row + c]! >= 0) return row + c;
  } else if (side === 1) {
    for (let r = 0; r < size; r++) if (cover[r * size + k]! >= 0) return r * size + k;
  } else if (side === 2) {
    const row = k * size;
    for (let c = size - 1; c >= 0; c--) if (cover[row + c]! >= 0) return row + c;
  } else {
    const col = size - 1 - k;
    for (let r = size - 1; r >= 0; r--) if (cover[r * size + col]! >= 0) return r * size + col;
  }
  return -1;
}

export interface LapResult {
  shots: number;
  ammoLeft: number;
  /** Step of the last shot (-1 when none). */
  lastStep: number;
}

/**
 * Run one full lap on `cover` (mutated). Stops early when ammo runs out. `out` collects the shots
 * when given (the scene animates them; the bot passes nothing).
 */
export function runLap(cover: Int8Array, size: number, color: number, ammo: number, out?: Shot[]): LapResult {
  let shots = 0;
  let lastStep = -1;
  const steps = size * 4;
  for (let p = 0; p < steps && ammo > 0; p++) {
    const idx = rayTarget(cover, size, p);
    if (idx >= 0 && cover[idx] === color) {
      cover[idx] = -1;
      ammo--;
      shots++;
      lastStep = p;
      if (out) out.push({ step: p, idx, color });
    }
  }
  return { shots, ammoLeft: ammo, lastStep };
}

/** Create a game from a level. `picture` is only needed for guessing. */
export function createGame(level: Level, picture: Picture | null = null): State {
  const cover = Int8Array.from(level.cover);
  let remaining = 0;
  for (const v of cover) if (v >= 0) remaining++;
  return {
    level,
    picture,
    cover,
    remaining,
    queues: level.queues.map((q) => q.map((s) => ({ ...s }))),
    tray: [],
    locks: 0,
    status: "playing",
    winBy: null,
    lostBy: null,
    launches: 0,
    shots: 0,
    wrong: 0,
    wrongTexts: [],
    bestLap: 0,
    guessPct: null,
    score: 0,
    history: [],
  };
}

/** Cheap copy for lookahead (shooters are replaced, never mutated in place). */
export function cloneState(s: State): State {
  return {
    ...s,
    cover: s.cover.slice(),
    queues: s.queues.map((q) => q.slice()),
    tray: s.tray.slice(),
    wrongTexts: s.wrongTexts,
    history: [],
  };
}

export function totalBlocks(s: State): number {
  return s.level.size * s.level.size;
}

export function clearedPct(s: State): number {
  return Math.floor((100 * (totalBlocks(s) - s.remaining)) / totalBlocks(s));
}

/** Tray slots that can still take a parking shooter (can be negative when locks bite a full tray). */
export function freeSlots(s: State): number {
  return s.level.trayCap - s.locks - s.tray.length;
}

export function shooterAt(s: State, from: "queue" | "tray", i: number): Shooter | undefined {
  return from === "queue" ? s.queues[i]?.[0] : s.tray[i];
}

export interface LaunchEval {
  legal: boolean;
  shots: number;
  ammoLeft: number;
  /** The shooter survives the lap without losing the game. */
  safe: boolean;
}

/** Evaluate a launch without touching the state. */
export function evalLaunch(s: State, from: "queue" | "tray", i: number, scratch?: Int8Array): LaunchEval {
  const sh = shooterAt(s, from, i);
  if (!sh) return { legal: false, shots: 0, ammoLeft: 0, safe: false };
  const cover = scratch ?? new Int8Array(s.cover.length);
  cover.set(s.cover);
  const lap = runLap(cover, s.level.size, sh.color, sh.ammo);
  if (from === "tray" && lap.shots === 0) return { legal: false, shots: 0, ammoLeft: lap.ammoLeft, safe: false };
  const won = s.remaining - lap.shots === 0;
  const free = freeSlots(s) + (from === "tray" ? 1 : 0);
  const safe = won || lap.ammoLeft === 0 || free > 0;
  return { legal: true, shots: lap.shots, ammoLeft: lap.ammoLeft, safe };
}

/** All launches that are legal right now. */
export function legalLaunches(s: State): Action[] {
  const out: Action[] = [];
  if (s.status !== "playing") return out;
  const scratch = new Int8Array(s.cover.length);
  for (let i = 0; i < s.queues.length; i++) if (s.queues[i]!.length && evalLaunch(s, "queue", i, scratch).legal) out.push({ t: "q", i });
  for (let i = 0; i < s.tray.length; i++) if (evalLaunch(s, "tray", i, scratch).legal) out.push({ t: "t", i });
  return out;
}

/**
 * Is there any launch that does not lose on the spot? When not, the position is "stuck": the
 * player can still name the picture (or restart) but every launch ends the run.
 */
export function isStuck(s: State): boolean {
  return s.status === "playing" && !hasSafeMove(s);
}

export function hasSafeMove(s: State): boolean {
  const scratch = new Int8Array(s.cover.length);
  for (let i = 0; i < s.queues.length; i++) if (s.queues[i]!.length) {
    const e = evalLaunch(s, "queue", i, scratch);
    if (e.legal && e.safe) return true;
  }
  for (let i = 0; i < s.tray.length; i++) {
    const e = evalLaunch(s, "tray", i, scratch);
    if (e.legal && e.safe) return true;
  }
  return false;
}

export interface ApplyOptions {
  /** Kept for bot call sites; a stuck position is no longer an automatic loss (see isStuck). */
  checkStuck?: boolean;
  /** Collect per-shot detail for the scene (default true). */
  detail?: boolean;
  /** Append to history (default true). */
  record?: boolean;
}

function finishScore(s: State): void {
  if (s.winBy === "guess") {
    const left = Math.round((100 * s.remaining) / totalBlocks(s));
    s.score = Math.max(50, 500 + 10 * left - 50 * s.wrong);
  } else if (s.winBy === "clear") {
    s.score = Math.max(50, 500 - 50 * s.wrong);
  }
}

/** Apply one action. Pure function of (state, action): same level + same actions => same result. */
export function applyAction(s: State, a: Action, opts: ApplyOptions = {}): ActionResult {
  const record = opts.record ?? true;
  if (s.status !== "playing") return { ok: false, reason: "over", kind: a.t === "g" ? "guess" : "launch", shots: [], shotCount: 0 };

  if (a.t === "g") return applyGuess(s, a.text, record);

  const from = a.t === "q" ? "queue" : "tray";
  const sh = shooterAt(s, from, a.i);
  if (!sh) return { ok: false, reason: "bad_index", kind: "launch", from, index: a.i, shots: [], shotCount: 0 };

  const size = s.level.size;
  const shots: Shot[] | undefined = opts.detail === false ? undefined : [];
  const lap = runLap(s.cover, size, sh.color, sh.ammo, shots);
  if (from === "tray" && lap.shots === 0) {
    return { ok: false, reason: "no_target", kind: "launch", from, index: a.i, shots: [], shotCount: 0 };
  }

  // Commit the launch.
  if (from === "queue") s.queues[a.i] = s.queues[a.i]!.slice(1);
  else s.tray.splice(a.i, 1);
  const after: Shooter = { ...sh, ammo: sh.ammo - lap.shots };
  s.remaining -= lap.shots;
  s.shots += lap.shots;
  s.launches++;
  if (lap.shots > s.bestLap) s.bestLap = lap.shots;
  if (record) s.history.push(a);

  const res: ActionResult = {
    ok: true,
    kind: "launch",
    from,
    index: a.i,
    shooter: { id: sh.id, color: sh.color, ammoBefore: sh.ammo, ammoAfter: after.ammo },
    shots: shots ?? [],
    shotCount: lap.shots,
    endStep: lap.lastStep,
  };

  if (s.remaining === 0) {
    s.status = "won";
    s.winBy = "clear";
    finishScore(s);
    res.won = true;
    res.outcome = "emptied";
    if (after.ammo === 0 && s.locks > 0) {
      s.locks--;
      res.releasedLock = true;
    }
    return res;
  }

  if (after.ammo === 0) {
    res.outcome = "emptied";
    if (s.locks > 0) {
      s.locks--;
      res.releasedLock = true;
    }
  } else if (freeSlots(s) > 0) {
    const at = from === "tray" ? Math.min(a.i, s.tray.length) : s.tray.length;
    s.tray.splice(at, 0, after);
    res.outcome = "parked";
    res.parkIndex = at;
  } else {
    s.status = "lost";
    s.lostBy = "tray_full";
    res.outcome = "lost";
    return res;
  }

  return res;
}

function applyGuess(s: State, text: string, record: boolean): ActionResult {
  const res: ActionResult = { ok: true, kind: "guess", shots: [], shotCount: 0 };
  const n = normalizeAnswer(text);
  if (!n || !s.picture) return { ok: false, reason: "empty_guess", kind: "guess", shots: [], shotCount: 0 };
  if (record) s.history.push({ t: "g", text });
  if (matchesAnswer(s.picture, text)) {
    s.status = "won";
    s.winBy = "guess";
    s.guessPct = clearedPct(s);
    finishScore(s);
    res.correct = true;
    res.won = true;
    return res;
  }
  res.correct = false;
  if (s.wrongTexts.some((w) => normalizeAnswer(w) === n)) {
    res.repeated = true; // echoing a wrong answer again is free
    return res;
  }
  s.wrongTexts = [...s.wrongTexts, text.trim()];
  s.wrong++;
  if (s.locks < s.level.trayCap) {
    s.locks++;
    res.locked = true;
  }
  return res;
}

/** Re-run a recorded game from scratch. Same level + same actions => identical final state. */
export function replay(level: Level, picture: Picture | null, actions: Action[]): State {
  const s = createGame(level, picture);
  for (const a of actions) {
    if (s.status !== "playing") break;
    applyAction(s, a);
  }
  return s;
}

/** Remaining ammo per color across queues and tray (== remaining blocks per color, by construction). */
export function ammoByColor(s: State): number[] {
  const out = new Array<number>(s.level.colors).fill(0);
  for (const q of s.queues) for (const sh of q) out[sh.color]! += sh.ammo;
  for (const sh of s.tray) out[sh.color]! += sh.ammo;
  return out;
}

export function blocksByColor(cover: ArrayLike<number>, colors: number): number[] {
  const out = new Array<number>(colors).fill(0);
  for (let i = 0; i < cover.length; i++) {
    const v = cover[i]!;
    if (v >= 0) out[v]!++;
  }
  return out;
}

/** Map stars: name the picture while little of it is cleared, without wrong answers. A full clear earns one. */
export function starsFor(s: State): 1 | 2 | 3 {
  if (s.winBy !== "guess" || s.guessPct === null) return 1;
  if (s.guessPct <= 40 && s.wrong === 0) return 3;
  return s.guessPct <= 70 ? 2 : 1;
}
